import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useGame } from '../context/GameContext';

const { FiCoffee, FiPlay, FiShoppingBag, FiUser, FiSettings, FiStar } = FiIcons;

function MenuScreen({ onNavigate }) {
  const { state } = useGame();

  const menuItems = [
    {
      icon: FiPlay,
      title: 'Start Brewing',
      subtitle: 'Begin your barista journey',
      action: () => onNavigate('game'),
      color: 'from-green-400 to-green-600'
    },
    {
      icon: FiShoppingBag,
      title: 'Coffee Shop',
      subtitle: 'Upgrade your equipment',
      action: () => onNavigate('shop'),
      color: 'from-blue-400 to-blue-600'
    },
    {
      icon: FiUser,
      title: 'Profile',
      subtitle: 'View achievements & stats',
      action: () => onNavigate('profile'),
      color: 'from-purple-400 to-purple-600'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-100 p-4">
      {/* Header */}
      <motion.div 
        className="text-center mb-8 pt-8"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <div className="flex items-center justify-center mb-4">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
            className="mr-3"
          >
            <SafeIcon icon={FiCoffee} className="text-6xl text-amber-600" />
          </motion.div>
          <h1 className="text-5xl font-bold bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent">
            Barista Master
          </h1>
        </div>
        <p className="text-gray-600 text-lg">Craft the perfect cup, one order at a time</p>
      </motion.div>

      {/* Player Stats */}
      <motion.div 
        className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 mb-6 mx-auto max-w-md shadow-lg"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.3 }}
      >
        <div className="flex justify-between items-center">
          <div className="text-center">
            <p className="text-2xl font-bold text-amber-600">Level {state.level}</p>
            <p className="text-sm text-gray-500">Barista Level</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-green-600">${state.coins}</p>
            <p className="text-sm text-gray-500">Coins</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-purple-600">{state.score}</p>
            <p className="text-sm text-gray-500">Best Score</p>
          </div>
        </div>
        
        {/* Experience Bar */}
        <div className="mt-4">
          <div className="flex justify-between text-sm text-gray-500 mb-1">
            <span>Experience</span>
            <span>{state.experience % 100}/100</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <motion.div 
              className="bg-gradient-to-r from-amber-400 to-orange-500 h-2 rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${(state.experience % 100)}%` }}
              transition={{ duration: 0.5, delay: 0.5 }}
            />
          </div>
        </div>
      </motion.div>

      {/* Menu Items */}
      <div className="space-y-4 max-w-md mx-auto">
        {menuItems.map((item, index) => (
          <motion.button
            key={item.title}
            className={`w-full bg-gradient-to-r ${item.color} p-6 rounded-2xl shadow-lg text-white`}
            whileHover={{ scale: 1.02, y: -2 }}
            whileTap={{ scale: 0.98 }}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 + index * 0.1 }}
            onClick={item.action}
          >
            <div className="flex items-center">
              <SafeIcon icon={item.icon} className="text-3xl mr-4" />
              <div className="text-left">
                <h3 className="text-xl font-bold">{item.title}</h3>
                <p className="text-white/80">{item.subtitle}</p>
              </div>
            </div>
          </motion.button>
        ))}
      </div>

      {/* Daily Challenge */}
      <motion.div 
        className="mt-8 max-w-md mx-auto bg-gradient-to-r from-yellow-400 to-orange-500 p-4 rounded-2xl shadow-lg text-white text-center"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8 }}
      >
        <SafeIcon icon={FiStar} className="text-2xl mb-2 mx-auto" />
        <h4 className="font-bold">Daily Challenge</h4>
        <p className="text-sm text-white/90">Serve 25 perfect lattes</p>
        <p className="text-xs text-white/80 mt-1">Reward: 50 coins + Vanilla Syrup unlock</p>
      </motion.div>
    </div>
  );
}

export default MenuScreen;